import React, { useState, useEffect, useContext } from 'react';
import { INIKRU_LOGO as inikruLogo } from '../utils/brandAssets';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, User, ChevronDown, LogOut, Settings } from 'lucide-react';
import Dropdown from './ui/Dropdown';
import Badge from './ui/Badge';
import Avatar from './ui/Avatar';
import AuthContext from '../context/AuthContext';

const HamburgerIcon = ({ open, ...props }) => (
  <motion.div
    className="space-y-1.5"
    animate={open ? 'open' : 'closed'}
    {...props}
  >
    <motion.span
      className="block h-0.5 w-6 bg-current"
      variants={{
        closed: { rotate: 0, y: 0 },
        open: { rotate: 45, y: 6 },
      }}
    />
    <motion.span
      className="block h-0.5 w-6 bg-current"
      variants={{
        closed: { opacity: 1 },
        open: { opacity: 0 },
      }}
    />
    <motion.span
      className="block h-0.5 w-6 bg-current"
      variants={{
        closed: { rotate: 0, y: 0 },
        open: { rotate: -45, y: -6 },
      }}
    />
  </motion.div>
);

const UniversalNavbar = () => {
  const { pathname } = useLocation();
  const { user, logoutUser } = useContext(AuthContext);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [notificationCount] = useState(3); // Mock notification count

  // Check if user is authenticated (on protected pages)
  const isAuthenticated = user !== null && !['/Login', '/login', '/Register', '/'].includes(pathname);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  // Handle scroll effect for navbar background
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Shared link / button styling — Shopfront language: flat, rounded-[10px],
  // Montserrat bold. Navbar CTAs are ink so the page's single crimson primary
  // CTA keeps the stage.
  const ghostBtn =
    'px-4 py-2 text-[13.5px] font-display font-bold rounded-[10px] text-ink transition-colors duration-200 hover:bg-black/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B40A0A]';
  const solidBtn =
    'rounded-[10px] px-5 py-2.5 text-[13.5px] font-display font-bold text-white bg-ink transition-colors duration-200 hover:bg-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B40A0A] focus-visible:ring-offset-2';

  // Navigation links based on current page
  const getNavigationLinks = () => {
    const isLoginPath = pathname === '/Login' || pathname === '/login';

    if (pathname === '/') {
      return (
        <Link to="/login" className={solidBtn} onClick={() => setIsMobileMenuOpen(false)}>
          Login
        </Link>
      );
    } else if (isLoginPath) {
      return (
        <>
          <Link to="/" className={ghostBtn} onClick={() => setIsMobileMenuOpen(false)}>
            Home
          </Link>
          <Link to="/Register" className={solidBtn} onClick={() => setIsMobileMenuOpen(false)}>
            Sign up free
          </Link>
        </>
      );
    } else if (pathname === '/Register') {
      return (
        <>
          <Link to="/" className={ghostBtn} onClick={() => setIsMobileMenuOpen(false)}>
            Home
          </Link>
          <Link to="/login" className={solidBtn} onClick={() => setIsMobileMenuOpen(false)}>
            Login
          </Link>
        </>
      );
    }
    return (
      <Link to="/Register" className={solidBtn} onClick={() => setIsMobileMenuOpen(false)}>
        Get Started
      </Link>
    );
  };

  // User dropdown menu items
  const getUserDropdownItems = () => {
    if (!isAuthenticated) return [];

    return [
      {
        label: 'Profile',
        icon: <User size={16} />,
        onClick: () => {
          window.location.href = '/Profile';
        },
      },
      {
        label: 'Settings',
        icon: <Settings size={16} />,
        onClick: () => {
          window.location.href = '/manage_subscription';
        },
      },
      { divider: true },
      {
        label: 'Logout',
        icon: <LogOut size={16} />,
        onClick: () => {
          if (logoutUser) logoutUser();
        },
      },
    ];
  };

  const navVariants = {
    hidden: { y: -100, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 120, damping: 20, delay: 0.1 },
    },
  };

  const mobileMenuVariants = {
    closed: {
      opacity: 0,
      height: 0,
      transition: { duration: 0.3, when: 'afterChildren' },
    },
    open: {
      opacity: 1,
      height: 'auto',
      transition: { duration: 0.3, when: 'beforeChildren', staggerChildren: 0.05 },
    },
  };

  const mobileLinkVariants = {
    closed: { opacity: 0, y: -10 },
    open: { opacity: 1, y: 0 },
  };

  return (
    <motion.nav
      className={`fixed w-full top-0 border-b-[1.5px] transition-all duration-300 ${
        isScrolled || isMobileMenuOpen
          ? 'bg-bone/95 backdrop-blur-md border-ink'
          : 'bg-bone/80 backdrop-blur-sm border-transparent'
      }`}
      style={{ zIndex: 'var(--z-sticky)' }}
      variants={navVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-[72px]">
          {/* Logo — the real INIKRU wordmark image, one identity everywhere */}
          <Link to="/" className="flex-shrink-0 z-20" aria-label="Inikru home">
            <img src={inikruLogo} alt="Inikru" className="h-[20px] md:h-[22px] w-auto block transition-opacity duration-300 hover:opacity-70" />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-1">
            {getNavigationLinks()}

            {/* Notification Bell - Only show when authenticated */}
            {isAuthenticated && (
              <button
                className="relative p-2.5 rounded-[10px] text-muted transition-colors duration-200 hover:bg-black/[0.04] hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B40A0A]"
                aria-label="Notifications"
              >
                <Bell size={20} />
                {notificationCount > 0 && (
                  <span className="absolute -top-1 -right-1">
                    <Badge variant="error" size="sm">
                      {notificationCount}
                    </Badge>
                  </span>
                )}
              </button>
            )}

            {/* User Profile Dropdown - Only show when authenticated */}
            {isAuthenticated && (
              <Dropdown
                trigger={
                  <button className="flex items-center space-x-2 px-3 py-2 rounded-[10px] transition-colors duration-200 hover:bg-black/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B40A0A]">
                    <Avatar
                      src={user?.profile_picture}
                      alt={user?.username || 'User'}
                      fallback={user?.username?.[0] || 'U'}
                      size="sm"
                    />
                    <span className="hidden lg:block text-sm font-semibold text-ink">
                      {user?.username || 'User'}
                    </span>
                    <ChevronDown size={16} className="text-muted" />
                  </button>
                }
                items={getUserDropdownItems()}
                align="right"
              />
            )}
          </div>

          {/* Mobile menu button */}
          <div
            className="md:hidden flex items-center space-x-2"
            style={{ zIndex: 'var(--z-sidebar-toggle)' }}
          >
            {/* Mobile Notification Bell - Only show when authenticated */}
            {isAuthenticated && (
              <button
                className="relative p-2 rounded-[10px] text-muted transition-colors hover:bg-black/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B40A0A]"
                aria-label="Notifications"
              >
                <Bell size={20} />
                {notificationCount > 0 && (
                  <span className="absolute -top-1 -right-1">
                    <Badge variant="error" size="sm">
                      {notificationCount}
                    </Badge>
                  </span>
                )}
              </button>
            )}

            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMobileMenuOpen(!isMobileMenuOpen);
              }}
              className={`p-2 rounded-[10px] transition-colors duration-200 touch-manipulation hover:bg-black/[0.04] active:bg-black/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B40A0A] ${
                isMobileMenuOpen ? 'text-primary' : 'text-ink'
              }`}
              style={{
                minHeight: '44px',
                minWidth: '44px',
                pointerEvents: 'auto',
              }}
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              <HamburgerIcon open={isMobileMenuOpen} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              className="md:hidden border-t border-line bg-bone/95 backdrop-blur-xl overflow-hidden"
              style={{ zIndex: 'var(--z-mobile-menu)' }}
              variants={mobileMenuVariants}
              initial="closed"
              animate="open"
              exit="closed"
            >
              <div className="px-4 py-6 space-y-3">
                {React.Children.map(getNavigationLinks(), (child, index) => (
                  child && React.isValidElement(child) ? (
                    <motion.div
                      key={index}
                      variants={mobileLinkVariants}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {React.cloneElement(child, {
                        className: `${child.props.className} block w-full text-center py-3 text-base touch-manipulation`,
                        style: {
                          ...(child.props.style || {}),
                          minHeight: '44px',
                          pointerEvents: 'auto',
                        },
                        onClick: (e) => {
                          e.stopPropagation();
                          if (child.props.onClick) {
                            child.props.onClick(e);
                          }
                          setIsMobileMenuOpen(false);
                        },
                      })}
                    </motion.div>
                  ) : null
                ))}

                {/* Mobile User Menu - Only show when authenticated */}
                {isAuthenticated && (
                  <motion.div variants={mobileLinkVariants} className="pt-4 border-t border-line">
                    <div className="flex items-center space-x-3 px-4 py-3 mb-3">
                      <Avatar
                        src={user?.profile_picture}
                        alt={user?.username || 'User'}
                        fallback={user?.username?.[0] || 'U'}
                        size="md"
                      />
                      <div>
                        <p className="text-sm font-semibold text-ink">
                          {user?.username || 'User'}
                        </p>
                        <p className="text-xs text-muted">
                          {user?.email || ''}
                        </p>
                      </div>
                    </div>
                    <Link
                      to="/Profile"
                      className="flex items-center space-x-3 px-4 py-3 rounded-[10px] text-ink transition-colors hover:bg-black/[0.04]"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <User size={18} />
                      <span className="text-sm font-medium">Profile</span>
                    </Link>
                    <Link
                      to="/manage_subscription"
                      className="flex items-center space-x-3 px-4 py-3 rounded-[10px] text-ink transition-colors hover:bg-black/[0.04]"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Settings size={18} />
                      <span className="text-sm font-medium">Settings</span>
                    </Link>
                    <button
                      onClick={() => {
                        if (logoutUser) logoutUser();
                        setIsMobileMenuOpen(false);
                      }}
                      className="flex items-center space-x-3 w-full px-4 py-3 rounded-[10px] text-red-600 transition-colors hover:bg-red-50"
                    >
                      <LogOut size={18} />
                      <span className="text-sm font-medium">Logout</span>
                    </button>
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default UniversalNavbar;
